// packages import
import { FC } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  tableCellClasses,
  styled
} from '@mui/material'

// components
import BillingTypeRow from './BillingTypeRow'

// others
import { BillableCasesByType } from '../../../../generated'
import { capitalizeCase } from '../../../utils'

interface BillableCasesTableProps {
  billableCases: BillableCasesByType | null
  loading: boolean
  hasFilters: boolean
  type: 'attorney' | 'client'
  selectedCases: string[]
  onSelectedCasesChange: (selectedCases: string[]) => void
}

const CustomTableRow = styled(TableRow)(({ theme }) => ({
  [`& .${tableCellClasses.head}`]: {
    color: 'white',
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0.5)
  }
}))

const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
  color: 'white',
  '&.Mui-checked': { color: 'white' },
  '&.MuiCheckbox-indeterminate': { color: 'white' }
}))

const BillableCasesTable: FC<BillableCasesTableProps> = ({
  type,
  billableCases,
  loading,
  hasFilters,
  selectedCases,
  onSelectedCasesChange
}) => {
  const billingTypes = [
    { key: 'flatFee', title: 'Flat Fee', cases: billableCases?.flatFee || [] },
    { key: 'hourly', title: 'Hourly', cases: billableCases?.hourly || [] },
    // For now, contingency final bills are created from case detail page
    // { key: 'contingency', title: 'Contingency', cases: billableCases?.contingency || [] },
    { key: 'hybrid', title: 'Hybrid', cases: billableCases?.hybrid || [] },
    // { key: 'contingencyHybrid', title: 'Contingency Hybrid', cases: billableCases?.contingencyHybrid || [] },
    { key: 'proBono', title: 'Pro bono', cases: billableCases?.proBono || [] },
    { key: 'consult', title: 'Consult', cases: billableCases?.consult || [] }
  ]
  const totalCases = billingTypes.reduce((sum, type) => sum + type.cases.length, 0)
  const allCaseIds = billingTypes.flatMap(type => type.cases.map(c => c.caseId))
  const allSelected = allCaseIds.length > 0 && allCaseIds.every(id => selectedCases.includes(id))
  const someSelected = allCaseIds.some(id => selectedCases.includes(id))

  const handleCaseSelect = (caseId: string, checked: boolean) => {
    if (checked) {
      onSelectedCasesChange([...selectedCases, caseId])
    } else {
      onSelectedCasesChange(selectedCases.filter(id => id !== caseId))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectedCasesChange(allCaseIds)
    } else {
      onSelectedCasesChange([])
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading billable cases...</Typography>
      </Box>
    )
  }

  if (!hasFilters) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography fontSize={18} color="text.secondary">
          Select an {capitalizeCase(type)} to view billable cases
        </Typography>
      </Box>
    )
  }

  if (totalCases === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography fontSize={18} color="text.secondary">
          No billable cases found
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2, pt: 5 }}>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <CustomTableRow>
              <TableCell width="3%">
                <CustomCheckbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={e => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell width="67%">Billable Matters</TableCell>
              <TableCell align="center" width="15%">
                Unbilled Hours
              </TableCell>
              <TableCell align="center" width="15%">
                Amount Due
              </TableCell>
            </CustomTableRow>
          </TableHead>
          <TableBody>
            {billingTypes.map(type => (
              <BillingTypeRow
                key={type.key}
                title={type.title}
                cases={type.cases}
                selectedCases={selectedCases}
                onCaseSelect={handleCaseSelect}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default BillableCasesTable
